import { createTheme, alpha } from '@mui/material/styles';

const sage = {
  50: '#F1F6F2',
  100: '#DCEADF',
  200: '#B8D5BE',
  300: '#8FBA98',
  400: '#6B9D75',
  500: '#4A7C59',
  600: '#3A6346',
  700: '#2D5036',
  800: '#21392A',
  900: '#15241B',
};

const coral = {
  300: '#F4B5A7',
  500: '#E8806F',
  600: '#D26755',
  700: '#B45041',
};

const neutral = {
  bg: '#F8F7F2',
  paper: '#FFFFFF',
  text: '#1F2937',
  textMuted: '#6B7280',
  border: '#E5E7EB',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      light: sage[300],
      main: sage[500],
      dark: sage[700],
      contrastText: '#FFFFFF',
    },
    secondary: {
      light: coral[300],
      main: coral[500],
      dark: coral[700],
      contrastText: '#FFFFFF',
    },
    background: {
      default: neutral.bg,
      paper: neutral.paper,
    },
    text: {
      primary: neutral.text,
      secondary: neutral.textMuted,
    },
    divider: neutral.border,
    success: { main: sage[500] },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, color: neutral.textMuted },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: neutral.bg,
          color: neutral.text,
          fontFeatureSettings: '"cv11", "ss01"',
        },
        '*': { boxSizing: 'border-box' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${neutral.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${neutral.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10 },
        containedPrimary: {
          backgroundColor: sage[500],
          '&:hover': { backgroundColor: sage[600] },
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: neutral.paper,
          borderBottom: `1px solid ${neutral.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: neutral.paper,
          borderRight: `1px solid ${neutral.border}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginInline: 8,
          paddingBlock: 8,
          '&.Mui-selected': {
            backgroundColor: alpha(sage[500], 0.12),
            color: sage[700],
            '&:hover': { backgroundColor: alpha(sage[500], 0.18) },
            '& .MuiListItemIcon-root': { color: sage[700] },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36, color: neutral.textMuted } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
  },
});

export const palette = { sage, coral, neutral };
